import {course_getCourses_Api} from 'apis'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useState} from 'react'
import {branch, compose, renderNothing} from 'recompose'
import {enrollment_status_options} from 'views/AppLocals/Course'
import AntdConfigWrapper, {RenderEmptyMeta,} from 'views/AppLocals/custom/AntdConfigWrapper'
import {reactSelectComponents, SelectWidget,} from 'views/AppLocals/custom/ReactSelectComponents'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'
import {LayoutContext} from '../../../../../components/layouts/Default/LayoutContext'

const FilterSelectField = ({
                               name,
                               value,
                               onChange,
                               options,
                               isLoading,
                               className,
                               ...props
                           }) => (
    <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
        <SelectWidget
            value={_.find(options, {value}) ? value : undefined}
            showSearch={true}
            allowClear={true}
            onChange={(value) => {
                onChange(name, value)
            }}
            loading={!!isLoading}
            optionFilterProp="label"
            className={classNames('CenterSelector w-full', className)}
            options={!!isLoading ? [] : options}
            suffixIcon={
                !!isLoading ? undefined : <reactSelectComponents.DropdownIndicator/>
            }
            {...props}
        />
    </AntdConfigWrapper>
)

const CourseAsyncWrapper = compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        renderNothing
    )
)(({owner_id, owner_type, children}) => {
    const [values, setValues] = useState()

    const apiInfo = course_getCourses_Api

    const {response, isLoading} = useAsyncWithCache({
        cacheId: [
            apiInfo.path.replace(':id', owner_id).replace(':type', owner_type),
            JSON.stringify(values),
        ].join('/'),
        apiInfo,
        values,
        deps: [values],
        query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
    })

    return _.isFunction(children)
        ? children({
            isLoading,
            items: getResponseItems(response),
            onSearch: (keyword) => {
                setValues({
                    keyword,
                })
            },
        })
        : children
})


const statusFilterOptions = [
    enrollment_status_options.pending,
    enrollment_status_options.identity_review,
    enrollment_status_options.ready_for_class,
    enrollment_status_options.completed,
    enrollment_status_options.rejected,
]

// Dùng chung với badge trạng thái ở danh sách Đơn đăng ký (renderCourseEnrollment.js) để màu
// pill khớp đúng màu filter theo từng trạng thái. `badge` = biến thể tĩnh (nền nhạt) cho chỗ chỉ
// hiển thị, không bấm.
export const statusPillStyles = {
    '-1': { active: 'bg-blue-500 border-blue-500 text-white',     idle: 'border-blue-300 text-blue-500 hover:bg-blue-50',     badge: 'bg-blue-50 border-blue-200 text-blue-600' },
    '1':  { active: 'bg-purple-500 border-purple-500 text-white', idle: 'border-purple-300 text-purple-500 hover:bg-purple-50', badge: 'bg-purple-50 border-purple-200 text-purple-600' },
    '2':  { active: 'bg-orange-500 border-orange-500 text-white', idle: 'border-orange-300 text-orange-500 hover:bg-orange-50', badge: 'bg-orange-50 border-orange-200 text-orange-600' },
    '9':  { active: 'bg-green-600 border-green-600 text-white',   idle: 'border-green-400 text-green-600 hover:bg-green-50',   badge: 'bg-green-50 border-green-200 text-green-600' },
    '-2': { active: 'bg-red-500 border-red-500 text-white',       idle: 'border-red-300 text-red-500 hover:bg-red-50',       badge: 'bg-red-50 border-red-200 text-red-500' },
}

export const CourseEnrollmentFilterByCourse = ({
                                                   owner_id,
                                                   owner_type,
                                                   defaultValue,
                                                   value: filter = {
                                                       status: undefined,
                                                   },
                                                   onChange: setValues = NullAsDefault,
                                               }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const handleChange = useCallback(
        (name, value) => {
            setValues({
                ...filter,
                [name]: value,
            })
        },
        [filter]
    )

    const statusSelectOptions = statusFilterOptions.map(({label, value}) => ({
        value: String(value),
        label: t(label),
    }))

    const currentStatus = filter.status !== undefined ? String(filter.status) : undefined

    if (isSm) {
        return (
            <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
                <SelectWidget
                    className="w-full"
                    allowClear={true}
                    value={currentStatus || undefined}
                    options={statusSelectOptions}
                    placeholder={t('all statuses')}
                    onChange={(value) => handleChange('status', value)}
                />
            </AntdConfigWrapper>
        )
    }

    return (
        <div className="flex flex-wrap gap-1.5">
            <button
                type="button"
                onClick={() => handleChange('status', undefined)}
                className={classNames(
                    'px-2.5 py-1 rounded-full text-xs font-medium border transition-all leading-tight',
                    currentStatus === undefined
                        ? 'bg-color-100 border-color-200 text-color-000'
                        : 'border-color-100 text-color-300 hover:bg-color-50'
                )}>
                {t('all')}
            </button>
            {statusFilterOptions.map(({label, value, icon}) => {
                const key = String(value)
                const isActive = currentStatus === key
                const style = statusPillStyles[key] ?? {}
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => handleChange('status', isActive ? undefined : key)}
                        className={classNames(
                            'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all leading-tight',
                            isActive ? style.active : style.idle
                        )}>
                        {icon}
                        {t(label)}
                    </button>
                )
            })}
        </div>
    )
}


export const CourseEnrollmentFilter = ({
                                           owner_id,
                                           owner_type,
                                           defaultValue,
                                           value: filter = {
                                               status: undefined,
                                               course_id: undefined,
                                           },
                                           onChange: setValues = NullAsDefault,
                                       }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const handleChange = useCallback(
        (name, value) => {
            setValues({
                ...filter,
                [name]: value,
            })
        },
        [filter]
    )

    const statusSelectOptions = statusFilterOptions.map(({label, value}) => ({
        value: String(value),
        label: t(label),
    }))

    return (
        <div className="flex flex-col space-y-2 mt-2">
            <CourseAsyncWrapper
                {...{
                    owner_id,
                    owner_type,
                }}>
                {({items, isLoading}) => {
                    const options = Array.from(items).map(({id, ...item}) => ({
                        value: id,
                        label: t(getTitle(item)),
                    }))
                    return (
                        <div className="flex flex-col gap-1">
                            <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                                {`${t('choose course')} `}
                            </span>
                            <FilterSelectField
                                name="course_id"
                                loading={isLoading}
                                value={filter.course_id}
                                options={options}
                                onChange={(name, value) => {
                                    handleChange(name, value)
                                }}
                                placeholder={t(isLoading ? 'loading...' : 'courses')}
                            />
                        </div>
                    )
                }}
            </CourseAsyncWrapper>

            <div className="flex flex-col gap-1.5">
                <span className="text-xs text-color-400 italic">
                    {`${t('status')} :`}
                </span>
                {isSm ? (
                    <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
                        <SelectWidget
                            className="w-full"
                            allowClear={true}
                            value={filter.status !== undefined ? String(filter.status) : undefined}
                            options={statusSelectOptions}
                            placeholder={t('all statuses')}
                            onChange={(value) => handleChange('status', value)}
                        />
                    </AntdConfigWrapper>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            type="button"
                            onClick={() => handleChange('status', undefined)}
                            className={classNames(
                                'px-2.5 py-1 rounded-full text-xs font-medium border transition-all leading-tight',
                                filter.status === undefined
                                    ? 'background-200 border-color-200 text-color-000'
                                    : 'border-color-100 text-color-300 hover:bg-color-50'
                            )}>
                            {t('all')}
                        </button>
                        {statusFilterOptions.map(({label, value, icon}) => {
                            const key = String(value)
                            const isActive = String(filter.status) === key
                            const style = statusPillStyles[key] ?? {}
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => handleChange('status', isActive ? undefined : key)}
                                    className={classNames(
                                        'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all leading-tight',
                                        isActive ? style.active : style.idle
                                    )}>
                                    {icon}
                                    {t(label)}
                                </button>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

export default CourseEnrollmentFilter
